import React, { useEffect, useState, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Space,
  Table,
  Tag,
  Progress,
  Spin,
  Divider,
  Button,
} from 'antd';
import {
  DollarSign,
  Clock,
  Users,
  Package,
  AlertCircle,
  ArrowRight,
  BarChart3,
  ShieldAlert,
  Layers,
  ShoppingBag,
  Target,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from 'recharts';
import {
  getFinance360Snapshot,
  Finance360SnapshotResponse,
} from '../../services/finance360Service';

const { Title, Text } = Typography;

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const Finance360: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [snapshot, setSnapshot] = useState<Finance360SnapshotResponse | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await getFinance360Snapshot({ topLimit: 5 });
        setSnapshot(data);
      } catch (error) {
        console.error("Error fetching Finance 360 data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value);
  };

  const funnelData = useMemo(() => {
    if (!snapshot?.salesCycle) return [];
    const stages = ['lead', 'discovery', 'qualified', 'negotiation', 'contract', 'onboarding', 'launched'];
    return stages.map(stage => ({
      name: stage.charAt(0).toUpperCase() + stage.slice(1),
      count: snapshot.salesCycle.stageCounts[stage] || 0
    }));
  }, [snapshot]);

  const inventoryPieData = useMemo(() => {
    if (!snapshot?.inventory) return [];
    return [
      { name: 'Vendable', value: snapshot.inventory.totals.sellableValue },
      { name: 'Réservé', value: snapshot.inventory.totals.reservedValue },
      { name: 'Défectueux', value: snapshot.inventory.totals.defectiveValue }
    ];
  }, [snapshot]);

  const salesTrendData = useMemo(() => {
    if (!snapshot?.salesTrend) return [];
    return snapshot.salesTrend.map((row) => ({
      month: row._id,
      sellIn: row.totalSellIn,
      netMargin: row.netMargin,
    }));
  }, [snapshot]);

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <Spin size="large" tip="Chargement de l'intelligence financière..." />
      </div>
    );
  }

  return (
    <div className="finance-360-container" style={{ padding: '24px 0' }}>
      <Space direction="vertical" size={24} style={{ width: '100%' }}>
        
        {/* Alerts Section - Uber/Qonto Style (Clean Banner) */}
        {(snapshot?.alerts.overduePos.length || 0) > 0 || (snapshot?.alerts.creditOverLimit.length || 0) > 0 ? (
          <Card 
            className="neo-card-alert" 
            style={{ 
              background: '#fff1f0', 
              border: '1px solid #ffa39e',
              borderRadius: '16px'
            }}
          >
            <Space align="start">
              <ShieldAlert size={24} color="#f5222d" />
              <div>
                <Text strong style={{ color: '#cf1322', fontSize: '16px' }}>Points d'attention prioritaires</Text>
                <Row gutter={24} style={{ marginTop: 8 }}>
                  {snapshot?.alerts.overduePos.length ? (
                    <Col>
                      <Text type="secondary">
                        <AlertCircle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> 
                        {snapshot.alerts.overduePos.length} Commandes en retard de paiement
                      </Text>
                    </Col>
                  ) : null}
                  {snapshot?.alerts.creditOverLimit.length ? (
                    <Col>
                      <Text type="secondary">
                        <Users size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> 
                        {snapshot.alerts.creditOverLimit.length} Clients au-dessus de leur limite de crédit
                      </Text>
                    </Col>
                  ) : null}
                </Row>
              </div>
            </Space>
          </Card>
        ) : null}

        {/* Cashflow & Working Capital */}
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={8}>
            <Card
              title={<Space><DollarSign size={20} /> <Text strong>Résumé Trésorerie</Text></Space>}
              bordered={false}
              style={{ borderRadius: '20px' }}
            >
              <Space direction="vertical" style={{ width: '100%' }} size={12}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text type="secondary">Entrées</Text>
                  <Text strong style={{ color: '#10b981' }}>{formatCurrency(snapshot?.overview.cashflow?.revenue || 0)}</Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text type="secondary">Sorties</Text>
                  <Text strong style={{ color: '#ef4444' }}>{formatCurrency(snapshot?.overview.cashflow?.expense || 0)}</Text>
                </div>
                <Divider style={{ margin: '4px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text strong>Net</Text>
                  <Text strong>{formatCurrency(snapshot?.overview.cashflow?.net || 0)}</Text>
                </div>
              </Space>
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card
              title={<Space><BarChart3 size={20} /> <Text strong>Receivables / Payables</Text></Space>}
              bordered={false}
              style={{ borderRadius: '20px' }}
            >
              <Space direction="vertical" style={{ width: '100%' }} size={12}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text type="secondary">À encaisser</Text>
                  <Text strong>{formatCurrency(snapshot?.receivablesPayables.totals.receivables || 0)}</Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text type="secondary">À payer</Text>
                  <Text strong>{formatCurrency(snapshot?.receivablesPayables.totals.payables || 0)}</Text>
                </div>
                <Divider style={{ margin: '4px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text strong>Net</Text>
                  <Text strong>{formatCurrency(snapshot?.receivablesPayables.net || 0)}</Text>
                </div>
              </Space>
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card
              title={<Space><Layers size={20} /> <Text strong>Working Capital</Text></Space>}
              bordered={false}
              style={{ borderRadius: '20px' }}
            >
              <Space direction="vertical" style={{ width: '100%' }} size={12}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text type="secondary">Stock valorisé</Text>
                  <Text strong>{formatCurrency(snapshot?.workingCapital.inventory.totalValue || 0)}</Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text type="secondary">BFR estimé</Text>
                  <Text strong>{formatCurrency(snapshot?.workingCapital.workingCapital || 0)}</Text>
                </div>
                <Divider style={{ margin: '4px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text type="secondary">DPO</Text>
                  <Text strong>{Math.round(snapshot?.dpo || 0)} j</Text>
                </div>
              </Space>
            </Card>
          </Col>
        </Row>

        {/* Sales Trend */}
        <Row gutter={[24, 24]}>
          <Col xs={24}>
            <Card
              title={<Space><BarChart3 size={20} /> <Text strong>Trend Sell-in & Net Margin</Text></Space>}
              bordered={false}
              style={{ borderRadius: '20px' }}
            >
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={salesTrendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <RechartsTooltip formatter={(val: number) => formatCurrency(val)} />
                  <Area type="monotone" dataKey="sellIn" stroke="#3b82f6" fill="#dbeafe" name="Sell-in" />
                  <Area type="monotone" dataKey="netMargin" stroke="#10b981" fill="#dcfce7" name="Net margin" />
                </AreaChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>

        {/* Executive Header - Minimalist Qonto Style */}
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={12} lg={6}>
            <Card className="neo-card-stat" bordered={false} style={{ borderRadius: '16px', height: '100%' }}>
              <Statistic 
                title={<Space><DollarSign size={16} /> <Text type="secondary">Sell-in YTD</Text></Space>}
                value={snapshot?.overview.orders.totalSellIn || 0}
                formatter={(val) => formatCurrency(val as number)}
                valueStyle={{ fontSize: '24px', fontWeight: 700 }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="neo-card-stat" bordered={false} style={{ borderRadius: '16px', height: '100%' }}>
              <Statistic 
                title={<Space><Target size={16} /> <Text type="secondary">Marge Nette</Text></Space>}
                value={snapshot?.overview.orders.netMargin || 0}
                formatter={(val) => formatCurrency(val as number)}
                valueStyle={{ fontSize: '24px', fontWeight: 700 }}
              />
              <Progress 
                percent={Math.round((snapshot?.overview.orders.netMargin || 0) / (snapshot?.overview.orders.totalSellIn || 1) * 100)}
                size="small" 
                strokeColor="#10b981"
                style={{ marginTop: 8 }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="neo-card-stat" bordered={false} style={{ borderRadius: '16px', height: '100%' }}>
              <Statistic 
                title={<Space><Clock size={16} /> <Text type="secondary">DSO (Délai Encaissement)</Text></Space>}
                value={snapshot?.overview.payments.avgPaymentDelayDays || 0}
                suffix="jours"
                valueStyle={{ fontSize: '24px', fontWeight: 700 }}
              />
              <Text type="secondary" style={{ fontSize: '12px' }}>Cible : 30 jours</Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="neo-card-stat" bordered={false} style={{ borderRadius: '16px', height: '100%' }}>
              <Statistic 
                title={<Space><Layers size={16} /> <Text type="secondary">BFR Estimé</Text></Space>}
                value={snapshot?.inventory.totals.totalValue || 0}
                formatter={(val) => formatCurrency(val as number)}
                valueStyle={{ fontSize: '24px', fontWeight: 700, color: '#f59e0b' }}
              />
              <Text type="secondary" style={{ fontSize: '12px' }}>Capitaux immobilisés</Text>
            </Card>
          </Col>
        </Row>

        {/* Main Content Row */}
        <Row gutter={[24, 24]}>
          {/* Sales Cycle Chart - Modern Area Chart */}
          <Col xs={24} lg={16}>
            <Card 
              title={<Space><BarChart3 size={20} /> <Text strong>Cycle de Closing & Funnel CRM</Text></Space>}
              bordered={false} 
              style={{ borderRadius: '20px' }}
            >
              <Row gutter={24}>
                <Col span={14}>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={funnelData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                      <XAxis type="number" hide />
                      <YAxis dataKey="name" type="category" width={100} axisLine={false} tickLine={false} />
                      <RechartsTooltip cursor={{fill: '#f8fafc'}} />
                      <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} barSize={20} />
                    </BarChart>
                  </ResponsiveContainer>
                </Col>
                <Col span={10}>
                  <div style={{ padding: '0 16px' }}>
                    <div style={{ marginBottom: 24 }}>
                      <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Délai Moyen Closing</Text>
                      <Title level={2} style={{ margin: '4px 0 0' }}>{snapshot?.salesCycle.closingCycle.averageDays || '--'} j</Title>
                    </div>
                    <div style={{ marginBottom: 24 }}>
                      <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Taux de Conversion</Text>
                      <Title level={2} style={{ margin: '4px 0 0', color: '#10b981' }}>{Math.round((snapshot?.salesCycle.totals.conversionRate || 0) * 100)}%</Title>
                    </div>
                    <Divider style={{ margin: '16px 0' }} />
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Text strong style={{ fontSize: '13px' }}>Conversion Étapes :</Text>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text type="secondary">Lead → Launch</Text>
                        <Text strong>{snapshot?.salesCycle.totals.launchedCount} / {snapshot?.salesCycle.totals.retailerCount}</Text>
                      </div>
                    </Space>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>

          {/* Inventory Valuation - Apple Style Minimalist Pie */}
          <Col xs={24} lg={8}>
            <Card 
              title={<Space><Package size={20} /> <Text strong>Valorisation Stock</Text></Space>}
              bordered={false} 
              style={{ borderRadius: '20px', height: '100%' }}
            >
              <div style={{ height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={inventoryPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {inventoryPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(val) => formatCurrency(val as number)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div style={{ marginTop: 16 }}>
                {inventoryPieData.map((item, index) => (
                  <div key={item.name} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <Space><div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: COLORS[index] }} /> <Text type="secondary">{item.name}</Text></Space>
                    <Text strong>{formatCurrency(item.value)}</Text>
                  </div>
                ))}
              </div>
            </Card>
          </Col>
        </Row>

        {/* Tables Section - Modern Transparent Style */}
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={12}>
            <Card 
              title={<Space><ShoppingBag size={20} /> <Text strong>Top Clients (Performance & Risque)</Text></Space>}
              bordered={false} 
              style={{ borderRadius: '20px' }}
              extra={<Button type="link" icon={<ArrowRight size={16} />}>Voir tout</Button>}
            >
              <Table 
                dataSource={snapshot?.topRetailers} 
                rowKey="retailerId" 
                pagination={false}
                size="middle"
                className="neo-table-simple"
                columns={[
                  {
                    title: 'Client',
                    dataIndex: 'retailerName',
                    render: (val, record) => (
                      <Space direction="vertical" size={0}>
                        <Text strong style={{ color: '#0f172a' }}>{val}</Text>
                        <Tag 
                          color={record.retailerSegment === 'A' ? 'gold' : 'blue'} 
                          bordered={false} 
                          style={{ fontSize: '10px', lineHeight: '16px', borderRadius: '4px' }}
                        >
                          SEGMENT {record.retailerSegment}
                        </Tag>
                      </Space>
                    )
                  },
                  {
                    title: 'CA',
                    dataIndex: 'totalSellIn',
                    align: 'right',
                    render: (val) => formatCurrency(val)
                  },
                  {
                    title: 'Délai Moy.',
                    dataIndex: 'avgPaymentDelayDays',
                    align: 'right',
                    render: (val) => (
                      <Text type={val > 45 ? 'danger' : 'secondary'}>{val || 0} j</Text>
                    )
                  },
                  {
                    title: 'En retard',
                    dataIndex: 'overdueAmount',
                    align: 'right',
                    render: (val) => val > 0 ? <Text strong color="#ef4444">{formatCurrency(val)}</Text> : <Text type="secondary">0 €</Text>
                  }
                ]}
              />
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card 
              title={<Space><Users size={20} /> <Text strong>Top Fournisseurs (Dépenses)</Text></Space>}
              bordered={false} 
              style={{ borderRadius: '20px' }}
              extra={<Button type="link" icon={<ArrowRight size={16} />}>Voir tout</Button>}
            >
              <Table 
                dataSource={snapshot?.topSuppliers} 
                rowKey="supplierId" 
                pagination={false}
                size="middle"
                className="neo-table-simple"
                columns={[
                  {
                    title: 'Fournisseur',
                    dataIndex: 'supplierName',
                    render: (val) => <Text strong>{val}</Text>
                  },
                  {
                    title: 'Dépenses',
                    dataIndex: 'totalSpend',
                    align: 'right',
                    render: (val) => formatCurrency(val)
                  },
                  {
                    title: 'Coût Import',
                    dataIndex: 'totalImportCost',
                    align: 'right',
                    render: (val) => formatCurrency(val)
                  },
                  {
                    title: 'Délai Paiement',
                    dataIndex: 'avgPaymentDelayDays',
                    align: 'right',
                    render: (val) => <Text type="secondary">{Math.round(val || 0)} j</Text>
                  }
                ]}
              />
            </Card>
          </Col>
        </Row>
      </Space>

      <style>{`
        .finance-360-container .ant-card {
          box-shadow: 0 4px 12px rgba(0,0,0,0.02);
          transition: all 0.3s ease;
        }
        .finance-360-container .ant-card:hover {
          box-shadow: 0 8px 24px rgba(0,0,0,0.05);
        }
        .neo-table-simple .ant-table-thead > tr > th {
          background: transparent;
          border-bottom: 1px solid #f1f5f9;
          font-weight: 600;
          color: #64748b;
          font-size: 12px;
          text-transform: uppercase;
        }
        .neo-table-simple .ant-table-cell {
          border-bottom: 1px solid #f1f5f9;
        }
        .neo-card-stat .ant-statistic-title {
          margin-bottom: 12px;
        }
      `}</style>
    </div>
  );
};

export default Finance360;
